import { ICycle, IExtendedUser, QuestionnaireType } from "src/common/types";

export enum SubmissionStatus {
  Open = "OPEN",
  Saved = "SAVED",
  Submitted = "SUBMITTED",
  Cancelled = "CANCELLED",
  Overdue = "OVERDUE"
}

export const submissionStatuses = {
  [SubmissionStatus.Open]: "open",
  [SubmissionStatus.Saved]: "saved",
  [SubmissionStatus.Submitted]: "submitted",
  [SubmissionStatus.Cancelled]: "cancelled",
  [SubmissionStatus.Overdue]: "overdue"
};

export enum TalkStatus {
  Open = "OPEN",
  Saved = "SAVED",
  Scheduled = "SCHEDULED",
  WaitingForDSVSignature = "WAITING_FOR_DSV_SIGNATURE",
  WaitingForICSignature = "WAITING_FOR_IC_SIGNATURE",
  Done = "DONE",
  Cancelled = "CANCELLED",
  Overdue = "OVERDUE"
}

export const talkStatuses = {
  [TalkStatus.Open]: "open",
  [TalkStatus.Saved]: "saved",
  [TalkStatus.Scheduled]: "scheduled",
  [TalkStatus.WaitingForDSVSignature]: "waitingForDSVSignature",
  [TalkStatus.WaitingForICSignature]: "waitingForICSignature",
  [TalkStatus.Done]: "done",
  [TalkStatus.Cancelled]: "cancelled",
  [TalkStatus.Overdue]: "overdue"
};

export enum PeerSelectionStatus {
  Open = "OPEN",
  Submitted = "SUBMITTED",
  Cancelled = "CANCELLED",
  Saved = "SAVED",
  Overdue = "OVERDUE"
}

export const peerSelectionStatuses = {
  [PeerSelectionStatus.Open]: "open",
  [PeerSelectionStatus.Submitted]: "submitted",
  [PeerSelectionStatus.Cancelled]: "cancelled",
  [PeerSelectionStatus.Saved]: "saved",
  [PeerSelectionStatus.Overdue]: "overdue"
};

export const OpenTasksSubmissionStatus = [
  SubmissionStatus.Open,
  SubmissionStatus.Saved,
  SubmissionStatus.Overdue,
  PeerSelectionStatus.Open,
  PeerSelectionStatus.Saved,
  PeerSelectionStatus.Overdue,
  TalkStatus.Open,
  TalkStatus.Saved,
  TalkStatus.Overdue
];

/**
 * Interfaces for peer feedback update
 */

export interface ITalkFeedbackGiver {
  id: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
}

export interface ITalkQuestionnaireBase {
  additionalComments?: string | null;
  id: string;
  questionnaireType: QuestionnaireType;
  submissionStatus: SubmissionStatus;
  submissionDueDate: Date | string;
  submissionDate: Date | string | null;
  feedbackGiver?: ITalkFeedbackGiver | IExtendedUser;
  questionnaireTemplateConfig?: string;
}

export interface ITalkQuestionnairePayload extends ITalkQuestionnaireBase {
  additionalComments: string | null;
  id: string;
  questionnaireType: QuestionnaireType;
  submissionStatus: SubmissionStatus;
  submissionDueDate: Date | string;
  submissionDate: Date | string | null;
  feedbackGiver: ITalkFeedbackGiver | IExtendedUser;
}

export interface ITalkQuestionnaire extends ITalkQuestionnairePayload {
  submissionDueDate: Date;
  submissionDate: Date | null;
}

export interface ITalkPeerSelectionPayload {
  status: PeerSelectionStatus;
  dueDate: string | Date;
  submissionDate: null | string | Date;
}

export interface ITalkPeerSelection extends ITalkPeerSelectionPayload {
  dueDate: Date;
  submissionDate: null | Date;
}

export interface ITalkBasePayload {
  cycle: ICycle;
  id: string;
  status?: TalkStatus;
  scheduledDate?: Date | string | null;
  talkCompletionDueDate?: Date | string;
  talkQuestionnaires: ITalkQuestionnaireBase[];
  participantId?: string;
  participantSignedDate?: Date | string | null;
  supervisorId?: string;
  supervisorSignedDate?: Date | string | null;
}

export interface ITalk extends ITalkPayload {
  scheduledDate: Date | null;
  talkCompletionDueDate: Date;
  supervisorSignedDate: Date | null;
  participantSignedDate: Date | null;
  talkQuestionnaires: ITalkQuestionnaire[];
  peerSelection: null | ITalkPeerSelection;
  peerSummaryCount?: {
    allPeerQuestionnairesCount: number;
    submittedPeerQuestionnairesCount: number;
  } | null;
}

export interface IExtendedTalk extends ITalk {
  supervisorDetails: IExtendedUser;
  participantDetails: IExtendedUser;
}

export interface ITalkPayload extends ITalkBasePayload {
  cycle: ICycle;
  id: string;
  status: TalkStatus;
  scheduledDate: Date | string | null;
  talkCompletionDueDate: Date | string;
  participantId: string;
  participantSignedDate: Date | string | null;
  supervisorId: string;
  supervisorSignedDate: Date | string | null;
  talkQuestionnaires: ITalkQuestionnairePayload[];
  peerSelection: null | ITalkPeerSelectionPayload;
}

/**
 * Interfaces for get talk ID (Deprecated)
 */

export interface IDeprecatedFeedbackPartyPayload {
  id: string;
  firstName: string;
  lastName: string;
  middleName: string | null;
  questionnaireType: QuestionnaireType;
  questionnaireSubmissionDate: Date | string | null;
  signedDate: Date | string | null;
  submissionStatus: SubmissionStatus;
  additionalComments: string | null;
}

export interface IDeprecatedFeedbackParty extends IDeprecatedFeedbackPartyPayload {
  questionnaireSubmissionDate: Date | null;
  signedDate: Date | null;
}

export interface IDeprecatedTalkPayload {
  id: string;
  status: TalkStatus;
  scheduledDate: Date | string | null;
  cycle: ICycle;
  submitter: IDeprecatedFeedbackPartyPayload | IDeprecatedFeedbackParty;
  receiver: IDeprecatedFeedbackPartyPayload | IDeprecatedFeedbackParty;
  questionnaireSubmissionDueDate: Date | string;
  talkCompletionDueDate: Date | string;
  startDate: Date | string;
}

export interface IDeprecatedTalk extends IDeprecatedTalkPayload {
  scheduledDate: Date | null;
  cycle: ICycle;
  submitter: IDeprecatedFeedbackParty;
  receiver: IDeprecatedFeedbackParty;
  questionnaireSubmissionDueDate: Date;
  talkCompletionDueDate: Date;
}

export interface IDeprecatedExtendedTalk extends IDeprecatedTalk {
  submitterDetails: IExtendedUser;
  receiverDetails: IExtendedUser;
}

type IFeedbackHistoryEntry = [string, ITalk[]];

export type IFeedbackHistory = IFeedbackHistoryEntry[];

export interface ISubmitSessionDatePayload {
  scheduledDate: Date | null;
}
